import { RemoteDiagnosticsSession, UseCaseTypesEnum } from '@vi-iot/client';
import { Observable } from 'rxjs';
import { ExtendedParametersTabsEnum } from '../remote-diagnostics-use-cases';
import { DiagnosticsOverlayTypeEnum } from '../../+installations/installation/installation-overlay.model';

export const backendStorageSessionsKey = 'RemoteDiagnosticsActiveSessions';
export const localStorageSessionsKey = 'isRemoteDiagnosticsRunning';
// below LL should work for every E3 Viessmann device
export const mainLogicalLink = 'LL_0000_Main_ECU_CAN01'; // used to fetch topology and discover other LL's in device
// due to incorrect architecture, we sometimes need to guess LL - below one has most probability of success
export const backupMainLogicalLink = 'LL_0002_HMU_CAN01'; // used if we have no idea which logical link to try
export const vddListAmountLimit = 10; // it is a limit how many vdd user can add to the list at once
export const establishingDiagnosticsConnectionTimeout = 60000; // 60s time after we raise error if connection is established
// VDD 1467 "SafetyRelevantRemoteUnlock" should be no longer possible to select - VIG-10778
export const vddToHide = 'SafetyRelevantRemoteUnlock';

export class DeviceSerialMismatchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DeviceSerialMismatchError';
  }
}

// Every new step needs to be added in enums' array in DiagnosticsConnectionBoxComponent
export enum DIAGNOSTICS_CONNECTION_STEP {
  GET_REASON_TO_CONNECT = 'GET_REASON_TO_CONNECT',
  GET_AVAILABLE_BACKEND_INSTANCE = 'GET_AVAILABLE_BACKEND_INSTANCE',
  WARNING = 'WARNING',
  RECONNECT = 'RECONNECT',
  CONNECTION_ESTABLISHING = 'CONNECTION_ESTABLISHING',
  LOGIN = 'LOGIN',
  OPEN_SESSION = 'OPEN_SESSION',
  SETUP_INFO = 'SETUP_INFO',
  REMOTE_VCI_REGISTER = 'REMOTE_VCI_REGISTER',
  REMOTE_VCI_AVAILABILITY_CHECK = 'REMOTE_VCI_AVAILABILITY_CHECK',
  REMOTE_VCI_ESTABLISHING_CONNECTION = 'REMOTE_VCI_ESTABLISHING_CONNECTION',
  MAIN_LOGICAL_LINK_OPEN_ATTEMPT = 'MAIN_LOGICAL_LINK_OPEN_ATTEMPT',
  PRODUCT_MATRIX_FETCH = 'PRODUCT_MATRIX_FETCH',
  DEVICE_MAIN_LOGICAL_LINK_OPEN_ATTEMPT = 'DEVICE_MAIN_LOGICAL_LINK_OPEN_ATTEMPT',
  SCAN_TOPOLOGY = 'SCAN_TOPOLOGY',
  MAIN_LOGICAL_LINK_CLOSE_ATTEMPT = 'MAIN_LOGICAL_LINK_CLOSE_ATTEMPT',
  ACTIVE_DTC_FETCH = 'ACTIVE_DTC_FETCH',
  MAIN_LOGICAL_LINK_FROM_TOPOLOGY_OPEN_ATTEMPT = 'MAIN_LOGICAL_LINK_FROM_TOPOLOGY_OPEN_ATTEMPT',
  LOAD_CONTROL_UNITS = 'LOAD_CONTROL_UNITS',
  CONNECTION_ACTIVE = 'CONNECTION_ACTIVE',
  CONNECTION_FAILED = 'CONNECTION_FAILED',
}

export enum DIAGNOSTICS_CONNECTION_STATUS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  LOADING = 'LOADING',
  FAILED = 'FAILED',
}

export enum DIAGNOSTICS_SUPPORTED_INTERVALS {
  // ms500 = 500, - for now not required
  s1 = 1000,
  s2 = 2000,
  s5 = 5000,
  s10 = 10000,
  s20 = 20000,
  s50 = 50000,
}

export interface RemoteDiagnosticsActiveSession {
  xAuthToken: RemoteDiagnosticsSession;
  sessionGuid: string; // used to close session in vnext
  diagStackNode: string;
  technicianWsUrl: string;
  vciResource: string;
  vciName: string;
  vinNumber: string;
  activeLogicalLinkNames: string[];
  currentProductMatrix: CurrentProductMatrix[];
  mainLogicalLinkFromTopology: string;
  busDetailsFromTopology: ActiveErrorWithDiagStackDetail[];
  gatewaySerial: string;
  deviceSerial: string; // real serial ( unchanged ) from device feature and not from vnext database ( in URL )
  selectedParameters: RemoteDiagnosticsParameter[];
  availableControlUnits: RemoteDiagnosticsControlUnit[];
  selectedControlUnit: RemoteDiagnosticsControlUnit;
  timestamp: string;
  lastUserActivityTimestamp: string;
  user: string;
  availableParametersForSelectedControlUnit: RemoteDiagnosticsVddListParameter[];
  selectedExtendedParametersTab: ExtendedParametersTabsEnum;
  selectedUseCaseType: UseCaseTypesEnum;
  selectedUseCaseGroupe: string;
  bundleId: string; // required for internal hydraulic scheme
  setupInfo: SetupInfo;
}

export interface RemoteDiagnosticsParameter {
  id: number;
  vddDetail: RemoteDiagnosticsVddListParameter;
  controlUnit: RemoteDiagnosticsControlUnit;
  requestId: string;
  interval: number;
  isSelected: boolean;
}

export interface RemoteDiagnosticsControlUnit {
  value: string;
  objectProperty: string;
  busType: string;
  busAddress: string;
}

export interface RemoteDiagnosticsVddListParameter {
  name: string;
  value: string;
  vddInterval: string; // this is kind of ID in CAN, not a cycle interval value!
  isWrite: boolean;
  isUseCaseVdd?: boolean;
  isRead: boolean;
}

export interface RemoteDiagnosticsChartValue {
  requestId: string;
  value: string | number;
  wsTime: number;
  unit: string;
}

export enum ServiceLayerVddListName {
  SystemConfigurationGeneral = 'SystemConfigurationGeneral',
  SystemConfigurationBoiler = 'SystemConfigurationBoiler',
  SystemConfigurationDHW = 'SystemConfigurationDHW',
  SystemConfigurationHeatingCircuits = 'SystemConfigurationHeatingCircuits',
  DiagnoseGeneral = 'DiagnoseGeneral',
  DiagnoseDHW = 'DiagnoseDHW',
  DiagnoseHeatingCircuit = 'DiagnoseHeatingCircuit',
}

export interface ServiceLayerVddPicker {
  vddListName: string;
  isSelected: boolean;
}

export interface BusDetails {
  PA_BusAddre: string;
  PA_BusType: string;
}

export interface BusDetailsWithLogicalLink extends BusDetails {
  logicalLink?: string;
}

export interface BusDetailsWithDtcError extends BusDetails {
  errorCode?: string;
}

export interface ActiveErrorWithDiagStackDetail extends BusDetails {
  logicalLink?: string;
  errorCode?: string;
  errorName?: string;
}

export interface DemoModeSelectedParameter {
  name: string;
  count: number;
}

export interface RunningParameterNameAndInterval {
  name: string;
  interval: number;
  requestId: string;
}

export interface HydraulicSchemeParams {
  bundleId: string;
  hydraulicMatrixVddDetails: unknown;
}

export interface ProductMatrixStructure {
  type: string;
  structure?: ProductMatrix[]; // new approach
  // below is legacy, and will be dropped at some point:
  logicalLinkName?: string; //  for old ODX -with no cascade devices -  but still possible
  ViessmannIdentificationNumber?: string; //  for old ODX -with no cascade devices -  but still possible
}

export interface ProductMatrix {
  productFamily: string;
  productNameTranslationId: string;
  logicalLinkName: string;
  ViessmannIdentificationNumber: string;
  busType: string;
  translationIdLogicalLinkName: string;
  busAddress: string;
}

export interface CurrentProductMatrix {
  logicalLinkName: string;
  viessmannIdentificationNumber: string;
}

export interface SetupInfo {
  otx: string;
  odx: string;
  'D-PDU-API': string;
  'Java Version': string;
  'API Version': string;
  type: string;
  'Runtime Simulation filepath': string;
  Transformator: string;
  'Runtime Manager': string;
  'JVM arch': string;
  Runtime: string;
  'JVM bit': string;
  javajob: string;
  'PDU API ini filepath': string;
  'Project path': string;
}

export interface DiagStackConnectionProperties {
  node: string;
  xAuthToken: string;
  vciName: string;
  vciResource: string;
}

export interface XAuthTokenAndDiagStackNode {
  node: string;
  xAuthToken: string;
}

export interface DiagStackBulkScheduleReadSchedulerOutcome {
  [key: string]: Observable<string>;
}

export interface DiagStackBulkScheduleReadSchedulerOutcomeResolved {
  [key: string]: string;
}

export interface RemoteDiagnosticsGuardStatus {
  isDiagnosticsSessionRunning: boolean;
  hasAccessToDiagnostics: boolean;
}
export interface RemoteDiagnosticsTab {
  type: DiagnosticsOverlayTypeEnum;
  title: string;
  active: boolean;
}
export const isUseCasePopupBlockedKey = 'isUseCasePopupBlocked';
